/**
 * Territory layout — the hex-grid arrangement that turns the constellation
 * into a "map of the system" (doc 147 §5.3 redesign). Each domain owns one
 * hex cell; entities of that domain are translated so their cluster centroid
 * lands on the cell centroid, preserving the intra-domain relative positions
 * the manifest's pre-computed forceAtlas2 layout produced.
 *
 * Pure and deterministic — same input list, same output map. The Sigma
 * renderer paints each polygon as a soft backdrop wash.
 */

export interface Vec2 {
  x: number;
  y: number;
}

export interface TerritoryLayout {
  /** Center of the hex cell — also where the domain's entity cluster lands. */
  centroid: Vec2;
  /** Six vertices of the pointy-top hexagon, clockwise from the top vertex. */
  polygon: Vec2[];
  /** Where to anchor the domain label (above the top vertex). */
  labelAnchor: Vec2;
}

export interface BuildTerritoryOptions {
  /** Cell radius (center to vertex). Default 100. */
  cellRadius?: number;
  /** Maximum columns before wrapping to a new row. Default = ceil(sqrt(N)). */
  columns?: number;
  /** Origin (top-left of the layout). Default { x: 0, y: 0 }. */
  origin?: Vec2;
}

const HEX_HEIGHT_FACTOR = Math.sqrt(3);

/**
 * Build a hex-grid territory layout for `domainIds`. Pointy-top hexes packed
 * with offset rows; centroids are exactly `sqrt(3) * cellRadius` apart for
 * every adjacent pair.
 */
export function buildTerritoryLayout(
  domainIds: string[],
  options: BuildTerritoryOptions = {},
): Record<string, TerritoryLayout> {
  const result: Record<string, TerritoryLayout> = {};
  if (domainIds.length === 0) return result;

  const r = options.cellRadius ?? 100;
  const columns = options.columns ?? Math.max(1, Math.ceil(Math.sqrt(domainIds.length)));
  const origin = options.origin ?? { x: 0, y: 0 };

  // Pointy-top hex grid: horizontal spacing sqrt(3)*r, vertical 1.5*r,
  // every other row offset by sqrt(3)/2 * r. This is the canonical packing
  // where each pair of touching cells is exactly sqrt(3)*r apart.
  const dx = HEX_HEIGHT_FACTOR * r;
  const dy = 1.5 * r;

  for (let i = 0; i < domainIds.length; i++) {
    const id = domainIds[i]!;
    const row = Math.floor(i / columns);
    const col = i % columns;
    const xOffset = row % 2 === 1 ? dx / 2 : 0;
    const cx = origin.x + col * dx + xOffset + r;
    const cy = origin.y + row * dy + r;
    result[id] = hexCell(cx, cy, r);
  }

  return result;
}

/**
 * Translate each entity's coords so its domain's cluster centroid lands on
 * its territory centroid. Preserves intra-domain relative positions exactly.
 * Entities without territories pass through unchanged; entities without coords
 * snap to their territory centroid.
 */
export function applyTerritoryToCoords(
  entities: Array<{ id: string; domain: string; x?: number; y?: number }>,
  territories: Record<string, TerritoryLayout>,
): Record<string, Vec2> {
  const out: Record<string, Vec2> = {};
  const byDomain = new Map<string, typeof entities>();
  for (const e of entities) {
    const arr = byDomain.get(e.domain);
    if (arr) arr.push(e);
    else byDomain.set(e.domain, [e]);
  }

  for (const [domain, group] of byDomain) {
    const territory = territories[domain];
    if (!territory) {
      for (const e of group) {
        if (typeof e.x === 'number' && typeof e.y === 'number') {
          out[e.id] = { x: e.x, y: e.y };
        }
      }
      continue;
    }
    const withCoords = group.filter(
      (e): e is { id: string; domain: string; x: number; y: number } =>
        typeof e.x === 'number' && typeof e.y === 'number',
    );
    if (withCoords.length === 0) {
      for (const e of group) {
        out[e.id] = { ...territory.centroid };
      }
      continue;
    }
    const cx = withCoords.reduce((s, e) => s + e.x, 0) / withCoords.length;
    const cy = withCoords.reduce((s, e) => s + e.y, 0) / withCoords.length;
    const tdx = territory.centroid.x - cx;
    const tdy = territory.centroid.y - cy;
    for (const e of group) {
      if (typeof e.x === 'number' && typeof e.y === 'number') {
        out[e.id] = { x: e.x + tdx, y: e.y + tdy };
      } else {
        out[e.id] = { ...territory.centroid };
      }
    }
  }

  return out;
}

/** Construct a pointy-top hex cell centered at (cx, cy) with radius r. */
function hexCell(cx: number, cy: number, r: number): TerritoryLayout {
  const polygon: Vec2[] = [];
  // Start at the top vertex (-π/2 in screen coords), rotate clockwise by π/3.
  for (let i = 0; i < 6; i++) {
    const angle = -Math.PI / 2 + i * (Math.PI / 3);
    polygon.push({
      x: cx + r * Math.cos(angle),
      y: cy + r * Math.sin(angle),
    });
  }
  return {
    centroid: { x: cx, y: cy },
    polygon,
    labelAnchor: { x: cx, y: cy - r * 1.2 },
  };
}
